import type { AdminUserProvisionInput } from '@atlas/models'
import { JobFunction, UserRole } from '@atlas/types'
import { useState } from 'react'
import {
	useChangeUserJobFunction,
	useChangeUserRole,
	useChangeUserStatus,
	useProvisionUser,
	useUsers,
} from '@/hooks/useUsers'
import { notification } from '@/notifications/notification-store'
import type { UserDTO } from '@/services/user.service'
import { describeUserMutationError, UserMutationKind } from './user-error'

/**
 * Non-JSX state + handlers for the SUPER_ADMIN user-management page, split by
 * concern (invite / role / job function / status) so each stays small. Each
 * mutation shows its result immediately and rolls back if the write is refused
 * (the optimistic patch lives in the `useUsers*` hooks).
 *
 * BOTH outcomes are reported to the app-wide status bar: every other surface
 * reports there, so a user learns one place to look and the message survives
 * navigating away. A failing dialog ALSO shows the reason in place and stays
 * open, so the retry control sits next to it — that is a supplement to the
 * status-bar report, never a replacement for it.
 */
type Report = (message: string) => void

function useInviteFlow(reportSuccess: Report, reportError: (error: unknown, kind: UserMutationKind) => void) {
	const provision = useProvisionUser()
	const [open, setOpen] = useState(false)
	const toggle = () => {
		provision.reset()
		setOpen((o) => !o)
	}
	const submit = (input: AdminUserProvisionInput) => {
		provision.mutate(input, {
			onSuccess: () => {
				reportSuccess(`Invitation sent to ${input.email}.`)
				setOpen(false)
			},
			onError: (error) => reportError(error, UserMutationKind.PROVISION),
		})
	}
	return { provision, open, toggle, submit }
}

function useRoleFlow(reportSuccess: Report, reportError: (error: unknown, kind: UserMutationKind) => void) {
	const changeRole = useChangeUserRole()
	const [target, setTarget] = useState<UserDTO | null>(null)
	const [selected, setSelected] = useState<UserRole>(UserRole.USER)
	const open = (user: UserDTO) => {
		changeRole.reset()
		setSelected(user.role as UserRole)
		setTarget(user)
	}
	const confirm = () => {
		if (!target) return
		const t = target
		changeRole.mutate(
			{ userId: t.userId, role: selected },
			{
				onSuccess: () => {
					reportSuccess(`Role updated for ${t.displayName}.`)
					setTarget(null)
				},
				onError: (error) => reportError(error, UserMutationKind.ROLE),
			},
		)
	}
	return { changeRole, target, selected, setSelected, open, close: () => setTarget(null), confirm }
}

function useJobFunctionFlow(reportSuccess: Report, reportError: (error: unknown, kind: UserMutationKind) => void) {
	const changeJobFunction = useChangeUserJobFunction()
	const [target, setTarget] = useState<UserDTO | null>(null)
	const [selected, setSelected] = useState<JobFunction>(JobFunction.UNASSIGNED)
	const open = (user: UserDTO) => {
		changeJobFunction.reset()
		setSelected(user.jobFunction as JobFunction)
		setTarget(user)
	}
	const confirm = () => {
		if (!target) return
		const t = target
		changeJobFunction.mutate(
			{ userId: t.userId, jobFunction: selected },
			{
				onSuccess: () => {
					reportSuccess(`Job function updated for ${t.displayName}.`)
					setTarget(null)
				},
				onError: (error) => reportError(error, UserMutationKind.JOB_FUNCTION),
			},
		)
	}
	return { changeJobFunction, target, selected, setSelected, open, close: () => setTarget(null), confirm }
}

function useStatusFlow(reportSuccess: Report, reportError: (error: unknown, kind: UserMutationKind) => void) {
	const changeStatus = useChangeUserStatus()
	const [target, setTarget] = useState<UserDTO | null>(null)
	const open = (user: UserDTO) => {
		changeStatus.reset()
		setTarget(user)
	}
	const confirm = () => {
		if (!target) return
		const t = target
		const nextActive = !t.isActive
		changeStatus.mutate(
			{ userId: t.userId, isActive: nextActive },
			{
				onSuccess: () => {
					reportSuccess(`${nextActive ? 'Activated' : 'Deactivated'} ${t.displayName}.`)
					setTarget(null)
				},
				onError: (error) => reportError(error, UserMutationKind.STATUS),
			},
		)
	}
	return { changeStatus, target, open, close: () => setTarget(null), confirm }
}

export function useUsersPageState() {
	const users = useUsers()
	const reportSuccess: Report = (message) => {
		notification.success(message)
	}
	const reportError = (error: unknown, kind: UserMutationKind) => {
		notification.error(describeUserMutationError(error, kind))
	}
	const invite = useInviteFlow(reportSuccess, reportError)
	const role = useRoleFlow(reportSuccess, reportError)
	const jobFunction = useJobFunctionFlow(reportSuccess, reportError)
	const status = useStatusFlow(reportSuccess, reportError)
	return { users, invite, role, jobFunction, status }
}
